import { inject, Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, ReplaySubject, switchMap } from 'rxjs';
import {
  IgnoredEntry,
  JobState,
  JobStatus,
  NumberReportEntry,
  PbxFields,
} from '../../shared/models/generated/ziroModels';
import { ActivatedRoute } from '@angular/router';
import { concatMap, delayWhen, filter, map, take, tap } from 'rxjs/operators';
import { MigrationsNumbersPollingContext } from './migrations-numbers-polling-context.service';

export type PbxType = 'cisco' | 'ring-central';

export interface MigrationsNumberReport<T extends PbxFields> {
  entries: NumberReportEntry<T>[];
  totalNumberOfCdrs: number;
  oldestCdrTimestamp: string;
  mostRecentCdrTimestamp: string;
}

@Injectable()
export class MigrationsNumberContext<T extends PbxFields> implements OnDestroy {
  private _http = inject(HttpClient);
  private _route = inject(ActivatedRoute);
  private _numbersPollingContext = inject(MigrationsNumbersPollingContext);

  private _stateSource = new ReplaySubject<MigrationsNumberReport<T>>(1);
  state$ = this._stateSource.asObservable();

  private _jobStateSource = new ReplaySubject<JobStatus>(1);
  jobState$ = this._jobStateSource.asObservable();

  private _baseUrl = `/services/microsoft/migrations/tenants/${this._route.snapshot.params.tenantId}/exports/${this._route.snapshot.data['pbxType']}/${this._route.snapshot.params.id}/number-report`;

  constructor() {
    this._init().subscribe();
  }

  ngOnDestroy(): void {
    this._numbersPollingContext.stopPolling();
  }

  refresh(): Observable<void> {
    return this._refresh().pipe(switchMap(() => this._init()));
  }

  updateTeamsUpn(rowIdToUpdate: string, teamsUpn: string): Observable<void> {
    return this._http
      .put<void>(`${this._baseUrl}/teams-upn/${rowIdToUpdate}`, { teamsUpn })
      .pipe(delayWhen(() => this.softRefresh()));
  }

  updateTeamsNumber(rowIdToUpdate: string, teamsNumber: string): Observable<void> {
    return this._http
      .put<void>(`${this._baseUrl}/teams-numbers/${rowIdToUpdate}`, { teamsNumber })
      .pipe(delayWhen(() => this.softRefresh()));
  }

  updateIgnoredDirectoryNumbers(ignoredEntries: IgnoredEntry[]): Observable<void> {
    return this._http
      .put<void>(this._baseUrl + '/ignored-entries', { ignoredEntries })
      .pipe(delayWhen(() => this.softRefresh()));
  }

  softRefresh(): Observable<MigrationsNumberReport<T>> {
    return this._get().pipe(
      tap((report) => {
        this._stateSource.next(report);
      }),
    );
  }

  private _init(): Observable<void> {
    this._numbersPollingContext.startPolling();

    return this._numbersPollingContext.state$.pipe(
      tap((status: JobStatus) => this._jobStateSource.next(status)),
      filter((status: JobStatus) => status.jobState === JobState.IDLE),
      concatMap((status: JobStatus) => {
        if (!status.lastRunTime) {
          return this._refresh();
        }
        this._numbersPollingContext.stopPolling();
        if (!status.error) {
          return this._get();
        }
        return of({
          entries: [],
          mostRecentCdrTimestamp: null,
          oldestCdrTimestamp: null,
          totalNumberOfCdrs: 0,
        }) as Observable<MigrationsNumberReport<T>>;
      }),
      filter((report: MigrationsNumberReport<T>) => !!report),
      map((report: MigrationsNumberReport<T>) => this._stateSource.next(report)),
      take(1),
    );
  }

  private _get(): Observable<MigrationsNumberReport<T>> {
    return this._http.get<MigrationsNumberReport<T>>(this._baseUrl);
  }

  private _refresh(): Observable<void> {
    return this._http.post<void>(this._baseUrl + '/refresh', null);
  }
}
